import { Enclave, FunctionType } from 'types';

import { useRelays } from '@api/relays';
import { RelayObject } from '@api/types';
import { useAsvTargetsInfiniteQuery } from '@components/features/AsvScans/queries';
import { AsvTarget } from '@components/features/AsvScans/types';
import { useOrigins } from '@components/features/Compliance/queries';
import { OriginType } from '@components/features/Compliance/types';
import {
  ENCLAVE_STATE_FILTERS,
  useEnclavesQuery,
} from '@components/features/Enclaves/queries';
import { useFunctionsQuery } from '@components/features/Functions/queries';
import { usePaginatedAcquirers } from '@components/features/Payments/Acquirers/queries';
import { Acquirer } from '@components/features/Payments/Acquirers/types';
import { usePaginatedMerchants } from '@components/features/Payments/Merchants/queries';
import { Merchant } from '@components/features/Payments/Merchants/types';
import { flattenInfiniteData } from '@utils/infinite-queries';

type UseResourcesReturn = {
  relays: RelayObject[];
  functions: FunctionType[];
  enclaves: Enclave[];
  origins: OriginType[];
  merchants: Merchant[];
  acquirers: Acquirer[];
  targets: AsvTarget[];

  isPending: boolean;
  /** @deprecated Use isPending instead */
  loading: boolean;
};

export function useResources(): UseResourcesReturn {
  const enclavesQuery = useEnclavesQuery(ENCLAVE_STATE_FILTERS.ACTIVE);
  const enclaves = enclavesQuery.data?.enclaves || [];
  const isEnclavesLoading = enclavesQuery.isInitialLoading;

  const functionsQuery = useFunctionsQuery();
  const functions = functionsQuery.data || [];
  const isFunctionsLoading = functionsQuery.isInitialLoading;

  const relaysQuery = useRelays();
  const relays = relaysQuery.data?.data || [];
  const isRelaysLoading = relaysQuery.isInitialLoading;

  const originsQuery = useOrigins();
  const origins = originsQuery.data?.origins || [];
  const isOriginsLoading = originsQuery.isInitialLoading;

  const merchantsQuery = usePaginatedMerchants();
  const merchants = merchantsQuery.data?.data || [];
  const isMerchantsLoading = merchantsQuery.isInitialLoading;

  const acquirersQuery = usePaginatedAcquirers();
  const acquirers = acquirersQuery.data?.data || [];
  const isAcquirersLoading = acquirersQuery.isInitialLoading;

  const targetsQuery = useAsvTargetsInfiniteQuery({
    retryOnMount: false,
  });
  const targets = flattenInfiniteData(targetsQuery);
  const isTargetsLoading = targetsQuery.isInitialLoading;

  const isLoading =
    isEnclavesLoading ||
    isFunctionsLoading ||
    isRelaysLoading ||
    isOriginsLoading ||
    isMerchantsLoading ||
    isAcquirersLoading ||
    isTargetsLoading;

  return {
    enclaves,
    relays,
    functions,
    origins,
    merchants,
    acquirers,
    targets,
    loading: isLoading,
    isPending: isLoading,
  };
}
